import React from 'react';
import { ConfirmModal, ConfirmModalProps } from '@ar/components/shared/ConfirmModal';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import { useInvoiceStore } from '@ar/stores/InvoicesStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refundPayment } from '@ar/network/AccountsReceivable.network';
import { toast, Typography } from '@notch-ordering/ui-components';
import CheckIcon from '@icons/check-icon.svg';
import { FETCH_TRANSACTION_ACTIVITY_QUERY_KEY, FETCH_TRANSACTION_QUERY_KEY,
    FETCH_TRANSACTIONS_QUERY_KEY } from '@ar/hooks/queries/InvoicesQueries.hook';
import { CurrencyInput } from '@ar/components/shared/InputCurrency';
import { getGpoId } from '@v2/utils/GPOUtils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from '@/constants/FeatureFlags';
import { centsToDollars, dollarsToCents, formatAsCurrency } from '@/utils/Formatters';

export type RefundCustomerModalProps = Partial<ConfirmModalProps>;

type RefundAmountValue = {
    amountDollars: number,
    amountCents: number,
};
export const RefundCustomerModal : React.FC<RefundCustomerModalProps> = (props) => {
    const { modals, setModal } = useInvoiceStore();
    const { supplierLoginData } = useSupplierStore();
    const gpoId = getGpoId(supplierLoginData);
    const queryClient = useQueryClient();
    const flags = useFlags<FeatureFlags>();
    const transaction = modals?.transaction;
    const [amount, setAmount] = React.useState<RefundAmountValue>({
        amountCents: 0,
        amountDollars: 0,
    });
    const [isFailed, setIsFailed] = React.useState<boolean>(false);
    const updateAmount = (valueDollars: number): void => {
        setAmount({
            amountDollars: valueDollars,
            amountCents: dollarsToCents(valueDollars),
        });
    };
    // Calculate total charge amount (invoice + surcharge)
    const totalChargeAmount = transaction?.paid
        ? transaction.paid + (transaction.surcharge_amount ?? 0)
        : 0;
    // Calculate maximum refundable amount based on feature flag
    // When flag is OFF (old behavior): only invoice amount (no surcharge)
    // When flag is ON (new behavior): total charge amount (invoice + surcharge)
    const maxRefundableAmount = flags?.refundWithSurcharge
        ? totalChargeAmount - (transaction?.refunded || 0)
        : (transaction?.paid || 0) - (transaction?.refunded || 0);
    const isAmountValid = maxRefundableAmount > 0 ? (amount.amountCents <= maxRefundableAmount) : true;
    const isValidForm = isAmountValid && amount.amountCents > 0;
    const hasSurcharge = transaction?.surcharge_amount && transaction.surcharge_amount > 0;

    const handleOnClose = ():void => {
        if (props?.onClose) props.onClose();
        setModal('refundCustomer', false);
    };

    const handleChangeAmount = (values): void => {
        updateAmount(values.floatValue);
        setIsFailed(false);
    };

    const refundCustomerMutation = useMutation(refundPayment, {
        onSuccess: () => {
            setIsFailed(false);
            toast.show({
                message: `${formatAsCurrency(amount.amountDollars)} refund processed for Invoice ${transaction?.invoice_number}`,
                icon: <CheckIcon/>,
            });
            queryClient.invalidateQueries([FETCH_TRANSACTIONS_QUERY_KEY]);
            queryClient.invalidateQueries([FETCH_TRANSACTION_ACTIVITY_QUERY_KEY]);
            queryClient.invalidateQueries([FETCH_TRANSACTION_QUERY_KEY]);
            handleOnClose();
        },
        onError: () => {
            setIsFailed(true);
        },
    });

    const handleOnConfirm = ():void => {
        if (props?.onConfirm) props.onConfirm();
        if (isValidForm) {
            refundCustomerMutation.mutate({
                supplierID: supplierLoginData?.supplier_id,
                gpoId,
                body: {
                    transaction_id: transaction?.id,
                    amount: amount.amountCents,
                },
            });
        }
    };

    React.useEffect(() => {
        if (!modals.refundCustomer) return;
        updateAmount(0);
    }, [modals.refundCustomer]);

    return <ConfirmModal
        onClose={handleOnClose}
        headerPadding="mt-0 mb-6"
        isLoading={refundCustomerMutation.isLoading}
        isOpen={modals.refundCustomer}
        title={`Confirm refund to be issued for invoice #${transaction?.invoice_number}`}
        prompt={<div>
            <CurrencyInput
                required
                variant="SMALL"
                isInvalid={!isAmountValid}
                onChange={handleChangeAmount}
                value={amount.amountDollars}
                label={'Amount'}/>
            <Typography className="mt-1">
                Paid amount {formatAsCurrency(centsToDollars(maxRefundableAmount))}
                {flags?.refundWithSurcharge && hasSurcharge && (
                    <span className="text-gray-600">
                        {' '}(Total charge: {formatAsCurrency(centsToDollars(totalChargeAmount))} = Invoice: {formatAsCurrency(centsToDollars(transaction.paid))} + Surcharge: {formatAsCurrency(centsToDollars(transaction.surcharge_amount))}
                        {transaction?.refunded && transaction.refunded > 0 && ` - Already refunded: ${formatAsCurrency(centsToDollars(transaction.refunded))}`})
                    </span>
                )}
            </Typography>
            {flags?.refundWithSurcharge && hasSurcharge && (
                <Typography className="mt-1 text-gray-600 text-sm">
                    This refund can include the surcharge amount that was charged with the invoice.
                </Typography>
            )}
            {!isAmountValid && <Typography className="mt-1 text-red-500">
                Refund amount cannot be greater than the maximum refundable amount{flags?.refundWithSurcharge && hasSurcharge ? ' (total charge including surcharge' : ''} minus already refunded{flags?.refundWithSurcharge && hasSurcharge ? ')' : ''}.
            </Typography>}
            {isFailed && <Typography className="mt-1 text-red-500">
                Failed to process refund
            </Typography>}
        </div>}
        onConfirm={handleOnConfirm}
        {...props}/>;
};
